/**
 * ProjectsPage — Enterprise project portfolio
 *
 * Ribbon: Novo Projeto | Atualizar | Cards/Tabela | Filtros | Exportar
 * KPI strip: Total · Críticos · Em Risco · Saudáveis
 * Content: grid de cards ou DataGrid
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquarePlus, RefreshCw, LayoutGrid, List, X, Search,
  FolderOpen, ChevronDown, CheckCircle2, Filter, Heart, Check,
} from 'lucide-react';
import { ModuleLayout } from '../components/layout/ModuleLayout';

import { EnterprisePage } from '../components/layout/EnterprisePage';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import {
  EnterpriseModal,
  ModalField,
  modalInputClass,
  modalSelectClass,
  ModalPrimaryButton,
  ModalSecondaryButton,
} from '../components/layout/EnterpriseModal';
import { EnterpriseRibbon } from '../components/layout/EnterpriseRibbon';
import { RibbonDesktopButton, desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';
import type { StatusCapsuleItem } from '../components/layout/StatusCapsule';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAppToast } from '../components/AppToast';
import { ProjectService } from '../services/projectService';
import { ProjectPlanService } from '../services/projectPlanService';
import type { ProjectFilters } from '../services/projectService';
import type { ProjectPlansSummary } from '../types/projectPlan';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
  PROJECT_HEALTH_LABELS,
  PROJECT_HEALTH_COLORS,
  PROJECT_HEALTH_DOT,
  VISIBILITY_LABELS,
} from '../types/enterpriseProject';
import type {
  EnterpriseProject,
  ProjectStatus,
  VisibilityScope,
  ProjectHealth,
} from '../types/enterpriseProject';
import { exportToCSV } from '../utils/exportTableData';

// ─── Constants ────────────────────────────────────────────────
const STATUS_OPTIONS: ProjectStatus[] = ['draft', 'active', 'on_hold', 'completed', 'archived'];
const VISIBILITY_OPTIONS: VisibilityScope[] = ['private', 'project', 'organization'];

// ─── Helpers ──────────────────────────────────────────────────
function fmtDate(v: string | null | undefined) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

// ─── Component ────────────────────────────────────────────────
export const ProjectsPage: React.FC = () => {
  const navigate    = useNavigate();
  const orgId       = useOrganizationId();
  const qc          = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();

  // ── View
  const [viewMode, setViewMode]         = useState<'cards' | 'table'>('table');
  const [selectedId, setSelectedId]     = useState<string | null>(null);

  // ── Filter state
  const [filterStatus, setFilterStatus] = useState<ProjectStatus | ''>('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterHealth, setFilterHealth] = useState<ProjectHealth | ''>('');

  // ── Create / edit form
  const [showModal, setShowModal]       = useState(false);
  const [editing, setEditing]           = useState<EnterpriseProject | null>(null);
  const [saving, setSaving]             = useState(false);
  const [formName, setFormName]         = useState('');
  const [formDesc, setFormDesc]         = useState('');
  const [formStatus, setFormStatus]     = useState<ProjectStatus>('draft');
  const [formVisibility, setFormVisibility] = useState<VisibilityScope>('organization');
  const [formStart, setFormStart]       = useState('');
  const [formTarget, setFormTarget]     = useState('');
  const [formObjective, setFormObjective] = useState<'process' | 'problem' | 'plan' | ''>('');

  const hasActiveFilters = filterStatus !== '' || filterSearch !== '' || filterHealth !== '';

  // ── Queries
  const filters = useMemo<ProjectFilters>(() => {
    const f: ProjectFilters = {};
    if (filterStatus) f.status = filterStatus;
    if (filterSearch) f.search = filterSearch;
    return f;
  }, [filterStatus, filterSearch]);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects', orgId, filters],
    queryFn: () => ProjectService.list(orgId, filters),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const { data: summariesById = {} } = useQuery({
    queryKey: ['project-plan-summaries', orgId],
    queryFn: () => ProjectPlanService.listProjectSummaries(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  // ── Compute health from plan summaries
  const computeHealthFromSummary = useCallback((p: EnterpriseProject, s?: ProjectPlansSummary): ProjectHealth => {
    const isOverdue = !!p.target_date && new Date(p.target_date) < new Date() && p.status !== 'completed' && p.status !== 'archived';
    const hasCritical = (s?.issues_critical ?? 0) > 0 || (s?.actions_overdue ?? 0) > 0;
    if (isOverdue && hasCritical) return 'critical';
    if (isOverdue || hasCritical) return 'warning';
    return 'healthy';
  }, []);

  // ── Derived health counts
  const healthCounts = useMemo(() => {
    const c = { critical: 0, warning: 0, healthy: 0 };
    projects.forEach(p => {
      c[computeHealthFromSummary(p, summariesById[p.id])]++;
    });
    return c;
  }, [projects, summariesById, computeHealthFromSummary]);

  // ── Health filter applied client-side
  const visibleProjects = useMemo(() => {
    if (!filterHealth) return projects;
    return projects.filter(p => computeHealthFromSummary(p, summariesById[p.id]) === filterHealth);
  }, [projects, summariesById, filterHealth, computeHealthFromSummary]);

  // ── Status capsules
  const statusItems = useMemo<StatusCapsuleItem[]>(() => [
    { label: 'Total',     value: projects.length,         color: 'slate' },
    { label: 'Críticos',  value: healthCounts.critical,   color: healthCounts.critical  > 0 ? 'red'     : 'slate' },
    { label: 'Em Risco',  value: healthCounts.warning,    color: healthCounts.warning   > 0 ? 'amber'   : 'slate' },
    { label: 'Saudáveis', value: healthCounts.healthy,    color: healthCounts.healthy   > 0 ? 'emerald' : 'slate' },
  ], [projects.length, healthCounts]);

  // ── Handlers
  const onRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['project-plan-summaries'] });
  }, [qc]);

  const resetForm = useCallback(() => {
    setEditing(null);
    setFormName(''); setFormDesc(''); setFormStatus('draft');
    setFormVisibility('organization'); setFormStart(''); setFormTarget('');
    setFormObjective('');
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setShowModal(true);
  }, [resetForm]);

  const openEdit = useCallback((p: EnterpriseProject) => {
    setEditing(p);
    setFormName(p.name);
    setFormDesc(p.description ?? '');
    setFormStatus(p.status);
    setFormVisibility(p.visibility_scope);
    setFormStart(p.start_date ?? '');
    setFormTarget(p.target_date ?? '');
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!formName.trim()) { showToast('warning', 'Nome é obrigatório'); return; }
    setSaving(true);
    try {
      if (editing) {
        await ProjectService.update(orgId, editing.id, {
          name: formName.trim(), description: formDesc.trim() || undefined,
          status: formStatus, start_date: formStart || undefined,
          target_date: formTarget || undefined, visibility_scope: formVisibility,
        });
        showToast('success', 'Projeto atualizado');
      } else {
        const userId = await ProjectService.getCurrentUserId();
        const newProject = await ProjectService.create(orgId, userId, {
          name: formName.trim(), description: formDesc.trim() || undefined,
          status: formStatus, start_date: formStart || undefined,
          target_date: formTarget || undefined, visibility_scope: formVisibility,
        });
        // Auto-create initial plan based on objective
        if (formObjective && newProject?.id) {
          const planMap: Record<string, { type: 'action' | 'process' | 'improvement'; name: string }> = {
            process: { type: 'process', name: 'Processo Principal' },
            problem: { type: 'improvement', name: 'Plano de Melhoria' },
            plan: { type: 'action', name: 'Plano de Ação Principal' },
          };
          const cfg = planMap[formObjective];
          if (cfg) {
            try {
              await ProjectPlanService.createPlan(orgId, { project_id: newProject.id, name: cfg.name, type: cfg.type });
            } catch { /* non-blocking */ }
          }
        }
        showToast('success', 'Projeto criado');
      }
      setShowModal(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project-plan-summaries'] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }, [editing, orgId, formName, formDesc, formStatus, formStart, formTarget, formVisibility, resetForm, qc, showToast]);

  const handleDelete = useCallback(async () => {
    const p = projects.find(x => x.id === selectedId);
    if (!p) return;
    if (!window.confirm(`Excluir projeto "${p.name}"?`)) return;
    try {
      await ProjectService.delete(orgId, p.id);
      setSelectedId(null);
      showToast('success', 'Projeto excluído');
      qc.invalidateQueries({ queryKey: ['projects'] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro');
    }
  }, [projects, selectedId, orgId, qc, showToast]);

  const clearFilters = useCallback(() => {
    setFilterStatus(''); setFilterSearch(''); setFilterHealth('');
  }, []);

  const setCards      = useCallback(() => setViewMode('cards'), []);
  const setTable      = useCallback(() => setViewMode('table'), []);
  const openSelected  = useCallback(() => { if (selectedId) navigate(`/projetos/${selectedId}`); }, [selectedId, navigate]);
  const editSelected  = useCallback(() => {
    const p = projects.find(x => x.id === selectedId);
    if (p) openEdit(p);
  }, [projects, selectedId, openEdit]);
  const EXPORT_COLUMNS = useMemo(() => [
    { key: 'Nome',          label: 'Nome' },
    { key: 'Status',        label: 'Status' },
    { key: 'Início',        label: 'Início' },
    { key: 'Meta',          label: 'Meta' },
    { key: 'Visibilidade',  label: 'Visibilidade' },
  ], []);

  const exportCsv = useCallback(() => {
    exportToCSV(
      projects.map(p => ({
        Nome: p.name, Status: PROJECT_STATUS_LABELS[p.status],
        Início: fmtDate(p.start_date), Meta: fmtDate(p.target_date),
        Visibilidade: VISIBILITY_LABELS[p.visibility_scope],
      })),
      EXPORT_COLUMNS,
      'projetos',
    );
    showToast('success', 'Exportado');
  }, [projects, EXPORT_COLUMNS, showToast]);

  // ── Filter dropdown state ──
  type ProjDd = 'status' | 'health' | null;
  const [openDd, setOpenDd] = useState<ProjDd>(null);
  const statusBtnRef = useRef<HTMLButtonElement>(null);
  const healthBtnRef = useRef<HTMLButtonElement>(null);
  const [ddPos, setDdPos] = useState({ top: 0, left: 0 });

  const toggleDd = useCallback((which: ProjDd) => {
    if (!which) return;
    const ref = which === 'status' ? statusBtnRef : healthBtnRef;
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setDdPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenDd(prev => prev === which ? null : which);
  }, []);

  useEffect(() => {
    if (!openDd) return;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-proj-dd]')) return;
      setOpenDd(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDd]);

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const ddPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const ddItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  // ── Ribbon (stable ReactNode memoized)
  const ribbon = useMemo(() => (
    <EnterpriseRibbon moduleId="projetos">
      <EnterpriseRibbon.Group>
        <RibbonDesktopButton icon={<MessageSquarePlus strokeWidth={1.5} />}      label="Projeto" onClick={openCreate} />
        <RibbonDesktopButton icon={<RefreshCw strokeWidth={1.5} />} label="Atualizar"    onClick={onRefresh} />
      </EnterpriseRibbon.Group>

      {selectedId && (
        <EnterpriseRibbon.Group>
          <RibbonDesktopButton icon={<FolderOpen strokeWidth={1.5} />}   label="Abrir"   onClick={openSelected} />
          <RibbonDesktopButton icon={<CheckCircle2 strokeWidth={1.5} />} label="Editar"  onClick={editSelected} />
          <RibbonDesktopButton icon={<X strokeWidth={1.5} />}            label="Excluir" onClick={handleDelete} />
        </EnterpriseRibbon.Group>
      )}

      <EnterpriseRibbon.Group>
        <RibbonDesktopButton icon={<LayoutGrid strokeWidth={1.5} />} label="Cards"  onClick={setCards}  active={viewMode === 'cards'} />
        <RibbonDesktopButton icon={<List strokeWidth={1.5} />}       label="Tabela" onClick={setTable}  active={viewMode === 'table'} />
        <RibbonDesktopButton icon={<ChevronDown strokeWidth={1.5} />} label="Exportar" onClick={exportCsv} />
      </EnterpriseRibbon.Group>

      <EnterpriseRibbon.Filters>
        {/* Status */}
        <button ref={statusBtnRef} data-proj-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('status')}>
          <Filter size={12} className="text-[var(--text-secondary)]" />
          <span>{filterStatus ? PROJECT_STATUS_LABELS[filterStatus] : 'Todos'}</span>
          <ChevronDown size={10} className="text-[var(--text-secondary)]" />
        </button>
        {openDd === 'status' && createPortal(
          <div data-proj-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
            <button type="button" className={ddItemClass} onClick={() => { setFilterStatus(''); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">Todos</span>
              <Check size={11} className={!filterStatus ? 'opacity-100' : 'opacity-0'} />
            </button>
            {STATUS_OPTIONS.map(s => (
              <button key={s} type="button" className={ddItemClass} onClick={() => { setFilterStatus(s); setOpenDd(null); }}>
                <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: PROJECT_STATUS_COLORS[s]?.bg ?? '#94a3b8' }} />
                <span className="flex-1">{PROJECT_STATUS_LABELS[s]}</span>
                <Check size={11} className={filterStatus === s ? 'opacity-100' : 'opacity-0'} />
              </button>
            ))}
          </div>,
          document.body
        )}
        {/* Health */}
        <button ref={healthBtnRef} data-proj-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('health')}>
          <Heart size={12} className="text-[var(--text-secondary)]" />
          <span>{filterHealth ? (filterHealth === 'critical' ? 'Crítico' : filterHealth === 'warning' ? 'Em Risco' : 'Saudável') : 'Todos'}</span>
          <ChevronDown size={10} className="text-[var(--text-secondary)]" />
        </button>
        {openDd === 'health' && createPortal(
          <div data-proj-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
            {[
              { value: '', label: 'Todos', color: '' },
              { value: 'critical', label: 'Crítico', color: '#ef4444' },
              { value: 'warning', label: 'Em Risco', color: '#f59e0b' },
              { value: 'healthy', label: 'Saudável', color: '#22c55e' },
            ].map(h => (
              <button key={h.value} type="button" className={ddItemClass} onClick={() => { setFilterHealth(h.value as ProjectHealth | ''); setOpenDd(null); }}>
                {h.color ? <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: h.color }} /> : <span className="inline-block h-[7px] w-[7px] shrink-0" />}
                <span className="flex-1">{h.label}</span>
                <Check size={11} className={filterHealth === h.value ? 'opacity-100' : 'opacity-0'} />
              </button>
            ))}
          </div>,
          document.body
        )}
        {/* Search */}
        <div className="flex w-[220px] shrink-0 items-center">
          <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
            <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
            <input
              type="text"
              placeholder="Buscar projeto..."
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
            />
            {filterSearch && (
              <button onClick={() => setFilterSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
                <X size={10} className="text-[var(--text-secondary)]" />
              </button>
            )}
          </div>
        </div>
      </EnterpriseRibbon.Filters>
    </EnterpriseRibbon>
  ), [
    openCreate, onRefresh,
    selectedId, openSelected, editSelected, handleDelete,
    viewMode, setCards, setTable,
    filterSearch, filterStatus, filterHealth, hasActiveFilters, clearFilters, exportCsv,
  ]);

  // ── Table columns
  const columns = useMemo<DataGridColumn<EnterpriseProject>[]>(() => [
    {
      key: 'name', label: 'Nome', width: '28%', sortable: true,
      render: r => r.name,
    },
    {
      key: 'status', label: 'Status', width: '11%', sortable: true,
      render: r => <EnterpriseBadge className={PROJECT_STATUS_COLORS[r.status]}>{PROJECT_STATUS_LABELS[r.status]}</EnterpriseBadge>,
    },
    {
      key: 'health', label: 'Saúde', width: '10%',
      render: r => {
        const h = computeHealthFromSummary(r, summariesById[r.id]);
        return <EnterpriseBadge className={PROJECT_HEALTH_COLORS[h]}>{PROJECT_HEALTH_LABELS[h]}</EnterpriseBadge>;
      },
    },
    {
      key: 'plans', label: 'Planos', width: '8%',
      render: r => <span className="font-mono text-[11px]">{summariesById[r.id]?.total_plans ?? 0}</span>,
    },
    {
      key: 'executions', label: 'Execuções', width: '10%',
      render: r => <span className="font-mono text-[11px]">{summariesById[r.id]?.executions_active ?? 0}</span>,
    },
    {
      key: 'actions_overdue', label: 'Ações Atrasadas', width: '12%',
      render: r => {
        const v = summariesById[r.id]?.actions_overdue ?? 0;
        return <span className={`font-mono text-[11px] ${v > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{v}</span>;
      },
    },
    {
      key: 'issues_critical', label: 'Problemas Críticos', width: '12%',
      render: r => {
        const v = summariesById[r.id]?.issues_critical ?? 0;
        return <span className={`font-mono text-[11px] ${v > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{v}</span>;
      },
    },
    {
      key: 'progress', label: 'Progresso', width: '10%',
      render: r => {
        const p = summariesById[r.id]?.progress ?? 0;
        return (
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-12 rounded-full bg-slate-100 dark:bg-slate-800">
              <div className={`h-1.5 rounded-full ${p >= 100 ? 'bg-emerald-500' : p > 0 ? 'bg-blue-500' : 'bg-slate-300'}`} style={{ width: `${Math.min(p, 100)}%` }} />
            </div>
            <span className="font-mono text-[11px]">{p}%</span>
          </div>
        );
      },
    },
    {
      key: 'target_date', label: 'Meta', width: '10%', mono: true,
      render: r => {
        const over = !!r.target_date && new Date(r.target_date) < new Date() && r.status !== 'completed';
        return <span className={over ? 'text-red-500' : ''}>{fmtDate(r.target_date)}</span>;
      },
    },
  ], [summariesById, computeHealthFromSummary]);

  // ─── Render ──────────────────────────────────────────────────
  return (
    <ModuleLayout ribbon={ribbon}>
      <EnterprisePage title="" subtitle="">

        {/* ── Content ── */}
        {viewMode === 'cards' ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {isLoading ? (
              <div className="col-span-full py-20 text-center text-[12px] text-slate-400">Carregando...</div>
            ) : visibleProjects.length === 0 ? (
              <div className="col-span-full flex flex-col items-center py-20 text-[12px] text-slate-400">
                <FolderOpen size={32} className="mb-2 text-slate-300 dark:text-slate-600" />
                <p>Nenhum projeto encontrado</p>
                <button onClick={openCreate} className="mt-2 text-[11px] text-blue-500 hover:underline">Criar projeto</button>
              </div>
            ) : (
              visibleProjects.map(p => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  summary={summariesById[p.id]}
                  health={computeHealthFromSummary(p, summariesById[p.id])}
                  isSelected={p.id === selectedId}
                  onSelect={() => setSelectedId(p.id === selectedId ? null : p.id)}
                  onOpen={() => navigate(`/projetos/${p.id}`)}
                />
              ))
            )}
          </div>
        ) : (
          <DataGridEnterprise
            columns={columns}
            data={visibleProjects}
            rowKey={r => r.id}
            isLoading={isLoading}
            emptyTitle="Nenhum projeto"
            emptySubtitle="Clique em Novo Projeto para começar"
            selectedKey={selectedId}
            onRowClick={r => setSelectedId(r.id === selectedId ? null : r.id)}
            onRowDoubleClick={r => navigate(`/projetos/${r.id}`)}
          />
        )}
      </EnterprisePage>

      {/* ── Create/Edit Modal ── */}
      <EnterpriseModal
        open={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editing ? 'Editar Projeto' : 'Novo Projeto'}
      >
        <div className="space-y-3">
          <ModalField label="Nome *">
            <input type="text" value={formName} onChange={e => setFormName(e.target.value)}
              placeholder="Nome do projeto" className={modalInputClass} autoFocus />
          </ModalField>
          <ModalField label="Descrição">
            <textarea value={formDesc} onChange={e => setFormDesc(e.target.value)}
              rows={3} className={`${modalInputClass} min-h-[60px] resize-y`} />
          </ModalField>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Status">
              <select value={formStatus} onChange={e => setFormStatus(e.target.value as ProjectStatus)} className={modalSelectClass}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{PROJECT_STATUS_LABELS[s]}</option>)}
              </select>
            </ModalField>
            <ModalField label="Visibilidade">
              <select value={formVisibility} onChange={e => setFormVisibility(e.target.value as VisibilityScope)} className={modalSelectClass}>
                {VISIBILITY_OPTIONS.map(v => <option key={v} value={v}>{VISIBILITY_LABELS[v]}</option>)}
              </select>
            </ModalField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Início">
              <input type="date" value={formStart} onChange={e => setFormStart(e.target.value)} className={modalInputClass} />
            </ModalField>
            <ModalField label="Meta">
              <input type="date" value={formTarget} onChange={e => setFormTarget(e.target.value)} className={modalInputClass} />
            </ModalField>
          </div>
          {!editing && (
            <ModalField label="Qual o objetivo do projeto?">
              <select value={formObjective} onChange={e => setFormObjective(e.target.value as typeof formObjective)} className={modalSelectClass}>
                <option value="">Nenhum (sem plano inicial)</option>
                <option value="process">Melhorar processo</option>
                <option value="problem">Resolver problema</option>
                <option value="plan">Executar plano de ação</option>
              </select>
            </ModalField>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <ModalSecondaryButton onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</ModalSecondaryButton>
          <ModalPrimaryButton onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : editing ? 'Atualizar' : 'Criar'}</ModalPrimaryButton>
        </div>
      </EnterpriseModal>

      <ToastContainer />
    </ModuleLayout>
  );
};

// ─── ProjectCard ──────────────────────────────────────────────

function ProjectCard({
  project, summary, health, isSelected, onSelect, onOpen,
}: {
  project: EnterpriseProject;
  summary?: ProjectPlansSummary;
  health: ProjectHealth;
  isSelected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}) {
  const isOverdue = !!project.target_date
    && new Date(project.target_date) < new Date()
    && project.status !== 'completed'
    && project.status !== 'archived';

  return (
    <div
      onClick={onSelect}
      onDoubleClick={onOpen}
      className={`group cursor-pointer rounded-md border p-3 transition-all ${
        isSelected
          ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-300 dark:border-blue-600 dark:bg-blue-900/10 dark:ring-blue-700'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900/50 dark:hover:border-slate-700'
      }`}
    >
      {/* Row 1 — name + health + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${PROJECT_HEALTH_DOT[health]}`} title={PROJECT_HEALTH_LABELS[health]} />
          <h3 className="line-clamp-1 text-[12px] font-semibold text-slate-800 dark:text-slate-200">{project.name}</h3>
        </div>
        <EnterpriseBadge className={`shrink-0 ${PROJECT_STATUS_COLORS[project.status]}`}>
          {PROJECT_STATUS_LABELS[project.status]}
        </EnterpriseBadge>
      </div>

      {/* Row 2 — description */}
      {project.description && (
        <p className="mt-1 line-clamp-2 text-[11px] text-slate-500 dark:text-slate-400">{project.description}</p>
      )}

      {/* Row 3 — progress bar */}
      <div className="mt-2">
        <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">
          <span>Progresso</span>
          <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{summary?.progress ?? 0}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={`h-1.5 rounded-full transition-all ${(summary?.progress ?? 0) >= 100 ? 'bg-emerald-500' : (summary?.progress ?? 0) > 0 ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-700'}`}
            style={{ width: `${Math.min(summary?.progress ?? 0, 100)}%` }}
          />
        </div>
      </div>

      {/* Row 4 — plan-based summary */}
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-slate-500 dark:text-slate-400">
        <div className="flex items-center justify-between">
          <span>Planos</span>
          <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{summary?.total_plans ?? 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Execuções</span>
          <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{summary?.executions_active ?? 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Ações atrasadas</span>
          <span className={`font-mono font-medium ${(summary?.actions_overdue ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>{summary?.actions_overdue ?? 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Problemas críticos</span>
          <span className={`font-mono font-medium ${(summary?.issues_critical ?? 0) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>{summary?.issues_critical ?? 0}</span>
        </div>
      </div>

      {/* Row 4 — status indicator */}
      <div className="mt-2 flex items-center justify-between">
        <EnterpriseBadge className={PROJECT_HEALTH_COLORS[health]}>
          {PROJECT_HEALTH_LABELS[health]}
        </EnterpriseBadge>
        {project.target_date && (
          <span className={`text-[10px] ${isOverdue ? 'font-medium text-red-500' : 'text-slate-400 dark:text-slate-500'}`}>
            Meta: {fmtDate(project.target_date)} {isOverdue && '⚠'}
          </span>
        )}
      </div>
    </div>
  );
}

export default ProjectsPage;
